import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';
import axios from 'axios';
import styles from './HomePage.module.scss';
import ProductCard from '../../components/common/ProductCard/ProductCard';
import productService from '../../services/productService';
import adminService from '../../services/adminService';
import StorySection from '../../components/common/StorySection/StorySection';
import RawMaterialSection from '../../components/common/RawMaterialSection/RawMaterialSection';
import FAQSection from '../../components/common/FAQSection/FAQSection';
import { 
  IoArrowForward, IoLeafOutline, IoRibbonOutline, IoShieldCheckmarkOutline, IoChevronBackOutline, IoChevronForwardOutline
} from 'react-icons/io5'; 
// [SEO] Import Helmet
import { Helmet } from 'react-helmet-async'; 
import { useLoading } from '../../context/LoadingContext/LoadingContext';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const BACKEND_URL = 'https://huongtramcomoc.vn';
const FB_APP_ID = '966242223397117';
const HeroImage = 'https://huongtramcomoc.vn/seo-banner.jpg'

// Mảng mô tả ngắn cho danh mục
const CATEGORY_DESCRIPTIONS = {
  "trầm nụ": "Lan tỏa an nhiên",
  "trầm bột": "Xông nhà tẩy uế",
  "trầm gỗ": "Vật phẩm phong thủy",
  "nước cất": "Tinh hoa chưng cất",
  "trà trầm": "Thanh lọc thân tâm",
  "vòng tay": "Bình an hộ mệnh",
  "default": "Tinh túy đất trời"
};

const getCategoryDesc = (name) => {
  if (!name) return CATEGORY_DESCRIPTIONS["default"];
  const key = name.toLowerCase();
  for (const k in CATEGORY_DESCRIPTIONS) {
    if (key.includes(k)) return CATEGORY_DESCRIPTIONS[k];
  }
  return CATEGORY_DESCRIPTIONS["default"];
};

const getFullImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
};

// SKELETON
const ProductSkeleton = () => (
  <div className={styles.skeletonCard}>
    <div className={`${styles.skeleton} ${styles.skeletonImg}`}></div>
    <div className={`${styles.skeleton} ${styles.skeletonText}`} style={{width: '70%'}}></div>
    <div className={`${styles.skeleton} ${styles.skeletonText}`} style={{width: '40%'}}></div>
  </div>
);

const CategorySkeleton = () => (
  <div className={styles.categoryCard}>
    <div className={`${styles.skeleton} ${styles.catImgSkeleton}`}></div>
    <div className={`${styles.skeleton} ${styles.skeletonText}`} style={{width: '50%', marginTop: '20px'}}></div>
  </div>
);

const HomePage = () => {
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoadingData(true);
        const [bestSellersRes, newArrivalsRes, categoriesRes, bannersRes] = await Promise.all([
          productService.getBestSellers(),
          productService.getNewArrivals(),
          adminService.getCategories(),
          axios.get(`${BACKEND_URL}/api/admin/banner_crud.php`)
        ]);

        setBestSellers(bestSellersRes.data);
        setNewArrivals(newArrivalsRes.data);
        setCategories(categoriesRes.data);
        
        // Lọc banner active
        const activeBanners = Array.isArray(bannersRes.data) ? bannersRes.data.filter(b => b.is_active == 1) : [];
        // Sắp xếp theo display_order nếu có
        activeBanners.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        setBanners(activeBanners);

      } catch (error) {
        console.error("Lỗi tải dữ liệu:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchAllData();
  }, []);

  // Slider Logic
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % banners.length), 7000);
    return () => clearInterval(interval);
  }, [banners.length]);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % banners.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + banners.length) % banners.length);

  // Swiper Settings
  const getProductSwiperSettings = (itemCount) => ({
    modules: [Navigation, Pagination, Autoplay],
    spaceBetween: 20,
    loop: itemCount > 4, 
    navigation: true, 
    pagination: { clickable: true },
    autoplay: { delay: 5000, disableOnInteraction: false },
    breakpoints: {
      0: { slidesPerView: 2.2, spaceBetween: 10, centeredSlides: false },
      640: { slidesPerView: 2.5, spaceBetween: 15 },
      1024: { slidesPerView: 4, spaceBetween: 30 }
    }
  });

  const categorySwiperSettings = {
    modules: [Navigation, Pagination],
    spaceBetween: 30,
    navigation: true, 
    pagination: { clickable: true },
    breakpoints: {
      0: { slidesPerView: 2.2, spaceBetween: 15, centeredSlides: true, loop: true },
      640: { slidesPerView: 3.5, spaceBetween: 20 },
      1024: { slidesPerView: 5, spaceBetween: 40 }
    }
  };

  // --- [SEO START] ---
  const pageUrl = "https://huongtramcomoc.vn/";
  const pageTitle = "Hương Trầm Cổ Mộc - Tinh Hoa Trầm Việt & Trà Trầm Tự Nhiên";
  const pageDescription = "Thương hiệu Dũng Như - Chuyên cung cấp Trầm hương tự nhiên, Trà Trầm, Nụ Trầm, Vòng tay Trầm Hương và các sản phẩm thủ công mỹ nghệ cao cấp. Cam kết 100% tự nhiên.";
  const seoImage = banners.length > 0 ? getFullImageUrl(banners[0].image_url) : "https://huongtramcomoc.vn/seo-banner.jpg";

  // Schema cho Trang chủ (Organization + WebSite)
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://huongtramcomoc.vn/#organization",
        "name": "Hương Trầm Cổ Mộc",
        "url": "https://huongtramcomoc.vn/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://huongtramcomoc.vn/logo.png",
          "width": 112,
          "height": 112
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+84-964-472-868",
          "contactType": "customer service",
          "areaServed": "VN",
          "availableLanguage": "Vietnamese"
        },
        "sameAs": [
          "https://www.facebook.com/huongtramcomoc",
          "https://zalo.me/0964472868"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://huongtramcomoc.vn/#website",
        "url": "https://huongtramcomoc.vn/",
        "name": "Hương Trầm Cổ Mộc",
        "description": "Tinh Hoa Trầm Việt & Trà Trầm Tự Nhiên",
        "publisher": { "@id": "https://huongtramcomoc.vn/#organization" },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://huongtramcomoc.vn/san-pham?search={search_term_string}",
          "query-input": "required name=search_term_string"
        },
        "inLanguage": "vi-VN"
      }
    ]
  };
  // --- [SEO END] ---

  return (
    <ParallaxProvider>
      <div className={styles.homePage}>
        {/* --- HELMET SEO --- */}
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={pageUrl} />
          
          {/* Open Graph (Facebook/Zalo) */}
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content={seoImage} />
          <meta property="og:site_name" content="Hương Trầm Cổ Mộc" />
          <meta property="og:locale" content="vi_VN" />
          <meta property="fb:app_id" content={FB_APP_ID} />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={seoImage} />

          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>
        {/* ----------------- */}

        {/* 1. HERO SECTION */}
        <section className={styles.heroSection}>
          {banners.length > 0 ? (
            <>
              {banners.map((banner, index) => {
                // LOGIC XỬ LÝ ẢNH & MÀU NỀN
                let bgStyle = { backgroundColor: '#2b1f16' }; // Màu nâu trầm mặc định (như trong ảnh)
                
                if (banner.image_url) {
                    const fullUrl = getFullImageUrl(banner.image_url);
                    bgStyle.backgroundImage = `url('${fullUrl}')`;
                }

                return (
                  <div key={banner.id} className={`${styles.heroSlide} ${index === currentSlide ? styles.active : ''}`}>
                    {/* Áp dụng style vừa tạo */}
                    <div className={styles.slideImage} style={bgStyle}></div>
                    
                    <div className={styles.overlay}></div>
                    <div className={styles.contentWrapper}>
                      <div className={styles.heroContent}>
                        <span className={styles.heroSubtitle}>— Tinh Hoa Đất Trời —</span>
                        <h1 className={styles.heroTitle}>{banner.title}</h1>
                        <Link to={banner.link_url || '/san-pham'} className={styles.ctaButton}>Khám Phá Ngay</Link>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {banners.length > 1 && (
                <div className={styles.navControls}>
                    <button onClick={prevSlide} className={styles.navArrow}><IoChevronBackOutline /></button>
                    <button onClick={nextSlide} className={styles.navArrow}><IoChevronForwardOutline /></button>
                </div>
              )}
            </>
          ) : (
             // FALLBACK KHI CHƯA CÓ BANNER NÀO
             <div className={`${styles.heroSlide} ${styles.active}`}>
                <div className={styles.slideImage} style={{backgroundColor: '#2b1f16' }}></div>
                <div className={styles.contentWrapper}>
                    <div className={styles.heroContent}>
                        <span className={styles.heroSubtitle}>— Hương Thơm Từ Tâm —</span>
                        <h1 className={styles.heroTitle}>Hương Trầm Cổ Mộc</h1>
                    </div>
                </div>
             </div>
          )}
        </section>

        <StorySection/>

        {/* 3. DANH MỤC SẢN PHẨM */}
        <section className={styles.categorySection}>
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <span className={styles.subHeader}>Bộ Sưu Tập</span>
              <h2 className={styles.titleSerif}>Danh Mục Sản Phẩm</h2>
              <p className={styles.description}>Hương thơm vĩnh cửu từ những khối trầm tích tụ linh khí đất trời qua ngàn năm.</p>
              <div className={styles.underline}></div>
            </div>
            
            <div className={styles.swiperWrapper}>
              {loadingData ? (
                <div className={styles.skeletonContainer}>{[1,2,3,4,5].map(i => <CategorySkeleton key={i}/>)}</div>
              ) : categories.length > 0 ? (
                <Swiper {...categorySwiperSettings} className={styles.customSwiper}>
                  {categories.map(cat => (
                    <SwiperSlide key={cat.id} className={styles.slideItem}>
                      <Link to={`/san-pham?category_id=${cat.id}`} className={styles.categoryCard}>
                        <div className={styles.catImgContainer}>
                          {/* [FIX] Xử lý URL ảnh danh mục */}
                          <img 
                            src={cat.image_url ? getFullImageUrl(cat.image_url) : '/placeholder.jpg'} 
                            alt={cat.name} 
                          />
                        </div>
                        <h3 className={styles.catName}>{cat.name}</h3>
                        <p className={styles.catDesc}>{getCategoryDesc(cat.name)}</p>
                      </Link>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : <p className={styles.emptyText}>Đang cập nhật danh mục...</p>}
            </div>
          </div>
        </section>

        <RawMaterialSection />

        {/* 5. BÁN CHẠY NHẤT */}
        <section className={styles.bestSellerSection}>
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.titleSerif}>Bán Chạy Nhất</h2>
              <p className={styles.description}>Những phẩm vật được khách hàng yêu thích và tin dùng, mang lại bình an và may mắn.</p>
              <div className={styles.underline}></div>
            </div>
            <div className={styles.swiperWrapper}>
              {loadingData ? (
                <div className={styles.skeletonGrid}>{[1,2,3,4].map(i => <ProductSkeleton key={i}/>)}</div>
              ) : bestSellers.length > 0 ? (
                <Swiper key={`bs-${bestSellers.length}`} {...getProductSwiperSettings(bestSellers.length)} className={styles.customSwiper}>
                  {bestSellers.map(p => (
                    <SwiperSlide key={p.id} className={styles.slideItem}><ProductCard product={p} /></SwiperSlide>
                  ))}
                </Swiper>
              ) : <p className={styles.emptyText}>Đang cập nhật sản phẩm...</p>}
            </div>
          </div>
        </section>

        {/* 6. SẢN PHẨM MỚI */}
        <section className={styles.newArrivalSection}>
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.titleSerif}>Sản Phẩm Mới</h2>
              <p className={styles.description}>Đánh thức khứu giác với những chế tác Trầm hương mới nhất từ nghệ nhân Cổ Mộc.</p>
              <div className={styles.underline}></div>
            </div>
            <div className={styles.swiperWrapper}>
              {loadingData ? (
                 <div className={styles.skeletonGrid}>{[1,2,3,4].map(i => <ProductSkeleton key={i}/>)}</div>
              ) : newArrivals.length > 0 ? (
                <Swiper key={`new-${newArrivals.length}`} {...getProductSwiperSettings(newArrivals.length)} className={styles.customSwiper}>
                  {newArrivals.map(p => (
                    <SwiperSlide key={p.id} className={styles.slideItem}><ProductCard product={p} /></SwiperSlide>
                  ))}
                </Swiper>
              ) : <p className={styles.emptyText}>Đang cập nhật sản phẩm...</p>}
            </div>
            <div className={styles.centerButton}>
              <Link to="/san-pham" className={styles.outlineButton}>Xem Tất Cả <IoArrowForward /></Link>
            </div>
          </div>
        </section>
        
        <FAQSection />
        
        {/* TRUST BADGES */}
        <section className={styles.trustSection}>
          <div className={styles.container}>
             <div className={styles.trustGrid}>
                <div className={styles.trustItem}>
                  <IoLeafOutline /> 
                  <h3>100% Tự Nhiên</h3>
                  <p>Cam kết nguồn nguyên liệu sạch từ rừng già Tiên Phước</p>
                </div>
                <div className={styles.trustItem}>
                  <IoRibbonOutline /> 
                  <h3>Thủ Công Tinh Xảo</h3>
                  <p>Chế tác bởi nghệ nhân làng nghề truyền thống</p>
                </div>
                <div className={styles.trustItem}>
                  <IoShieldCheckmarkOutline /> 
                  <h3>Bảo Hành Mùi Hương</h3>
                  <p>Cam kết chất lượng trọn đời sản phẩm</p>
                </div>
             </div>
          </div>
        </section>
      </div>
    </ParallaxProvider>
  );
};

export default HomePage;